import moment from 'moment'
import { CurrencyType } from 'features/Programs/Shared/types'
import { ConversionRates, formatCurrency } from 'shared/CurrencyFunctions'
import {
  BaseProgram,
  Mode,
  PreTaxProgram,
  ProgramType,
  TripMonth,
} from 'redux/employeePlatform/employeePlatformTypes'
import { EmployeeTransitPass } from 'redux/transitPass/transitPassTypes'
import {
  getProgramDisplayName,
  isEmployeeVisibleProgram,
} from 'employee-platform/shared/utils'
import {
  cycleDisplayBudget,
  isProratedBudget,
} from 'employee-platform/shared/programBudget'

export interface BudgetRow {
  id: string
  name: string
  spent: number
  remaining: number
  resetsInDays: number | null
  kind: string
  /** GRH budgets are trip counts. Every other row is cents. */
  unit: 'money' | 'trips'
  /** Starting amount is below the card program budget. */
  prorated: boolean
}

const TRANSIT_MODE_TYPES = new Set([
  'TRANSIT',
  'BUS',
  'COMMRAIL',
  'HEAVYRAIL',
  'LITERAIL',
  'FERRY',
  'AERIAL',
  'FUNICULAR',
  'MONORAIL',
])

const BIKE_MODE_TYPES = new Set(['BIKE', 'EBIKE', 'BIKESHARE', 'EBIKESHARE'])

export interface WalletTransitCard {
  id: string
  name: string
  iconUrl: string | null
  balanceCents: number | null
  cardNumber: string | null
}

const formatCardNumber = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  if (!trimmed) return null
  const compact = trimmed.replace(/\s+/g, '')
  if (!/^\d+$/.test(compact) || compact.length % 4 !== 0) return trimmed
  return compact.match(/.{1,4}/g)?.join(' ') || trimmed
}

export const walletTransitCards = (
  programs: BaseProgram[],
  passes: EmployeeTransitPass[] | null
): WalletTransitCard[] => {
  if (!passes?.length) return []

  const agencies = new Map<string, { name: string; iconUrl: string | null }>()
  programs
    .filter(
      (program) =>
        program.type === 'TRANSIT_PASS' &&
        isEmployeeVisibleProgram(program.status)
    )
    .forEach((program) => {
      ;(program.transitAgencies ?? []).forEach((agency) => {
        agencies.set(agency.id, {
          name: agency.name,
          iconUrl: agency.iconUrl?.trim() || null,
        })
      })
    })

  return passes
    .map((pass) => {
      const agency = agencies.get(pass.transitAgency)
      return {
        id: pass.id,
        name: pass.transitAgencyName || agency?.name || 'Transit Card',
        iconUrl: pass.transitAgencyIconUrl?.trim() || agency?.iconUrl || null,
        balanceCents: pass.balanceCents ?? null,
        cardNumber: formatCardNumber(pass.transitPassNumber),
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

export const cardLast4 = (value: string | null | undefined): string | null => {
  if (!value) return null
  const compact = value.replace(/\s+/g, '')
  if (!compact) return null
  return compact.slice(-4)
}

const daysUntil = (date: moment.MomentInput): number =>
  Math.max(0, moment(date).startOf('day').diff(moment().startOf('day'), 'days'))

export const monthlyBudgetRows = (input: {
  programs: BaseProgram[]
  transit: PreTaxProgram | null
  parking: PreTaxProgram | null
  isContractor: boolean
}): BudgetRow[] => {
  const rows: BudgetRow[] = []
  const monthReset = daysUntil(moment().endOf('month'))

  if (!input.isContractor) {
    if (input.transit) {
      rows.push({
        id: 'qtf-transit',
        name: 'Pre-Tax Transit',
        spent: Math.max(0, input.transit.cycleSpend ?? 0),
        remaining: Math.max(0, input.transit.cycleBalance ?? 0),
        resetsInDays: monthReset,
        kind: 'QTF',
        unit: 'money',
        prorated: false,
      })
    }
    if (input.parking) {
      rows.push({
        id: 'qtf-parking',
        name: 'Pre-Tax Parking',
        spent: Math.max(0, input.parking.cycleSpend ?? 0),
        remaining: Math.max(0, input.parking.cycleBalance ?? 0),
        resetsInDays: monthReset,
        kind: 'QTF',
        unit: 'money',
        prorated: false,
      })
    }
  }

  input.programs
    .filter(
      (program) =>
        program.budgetRequired === true &&
        isEmployeeVisibleProgram(program.status)
    )
    .sort((a, b) =>
      getProgramDisplayName(a).localeCompare(getProgramDisplayName(b))
    )
    .forEach((program) => {
      const resetsInDays = program.cycleEnd ? daysUntil(program.cycleEnd) : null
      if (program.type === 'GRH') {
        const allowance = Math.max(0, program.numUses ?? 0)
        const remaining =
          program.remainingUses == null
            ? allowance
            : Math.max(0, program.remainingUses)
        rows.push({
          id: program.id,
          name: getProgramDisplayName(program),
          spent: Math.max(0, allowance - remaining),
          remaining,
          resetsInDays,
          kind: program.type,
          unit: 'trips',
          prorated: false,
        })
        return
      }
      const budget = cycleDisplayBudget(program)
      const remaining =
        program.remainingBudget == null ? budget : program.remainingBudget
      rows.push({
        id: program.id,
        name: getProgramDisplayName(program),
        spent: Math.max(0, budget - remaining),
        remaining: Math.max(0, remaining),
        resetsInDays,
        kind: program.type,
        unit: 'money',
        prorated: isProratedBudget(program),
      })
    })

  return rows
}

export interface MyProgramTag {
  id: string
  label: string
  iconUrl: string | null
}

export interface MyProgramRow {
  id: string
  name: string
  detail: string
  tags: MyProgramTag[]
  pill: 'pre-tax' | 'employer-paid'
  enrollment: 'enrolled' | 'available'
  type: ProgramType | 'QTF'
  couponCode: string | null
}

const PROGRAM_TYPE_ORDER: Array<ProgramType | 'QTF'> = [
  'QTF',
  'MICROMOBILITY',
  'GRH',
  'OTP',
  'FLEX',
  'TRANSIT_PASS',
  'COUPON',
  'REPORTED_USAGE',
]

const CYCLE_PERIOD: { [cycle: string]: string } = {
  DAILY: 'day',
  MONTHLY: 'month',
  QUARTERLY: 'quarter',
  YEARLY: 'year',
}

const periodFor = (program: BaseProgram): string =>
  CYCLE_PERIOD[program.budgetCycle || ''] || 'month'

export const formatDashboardMoney = (
  cents: number,
  currency: CurrencyType = 'USD',
  conversionRates: ConversionRates = {},
  decimals = 2
): string => formatCurrency(currency || 'USD', cents, conversionRates, decimals)

const moneyDetail = (
  cents: number,
  period: string,
  currency: CurrencyType,
  conversionRates: ConversionRates
): string =>
  `${formatDashboardMoney(cents, currency, conversionRates)}/${period} budget`

const programDetail = (
  program: BaseProgram,
  currency: CurrencyType,
  conversionRates: ConversionRates
): string => {
  const period = periodFor(program)
  if (program.type === 'GRH' && program.numUses != null) {
    const noun = program.numUses === 1 ? 'trip' : 'trips'
    return `${program.numUses} ${noun}/${period}`
  }
  if (program.type === 'COUPON') {
    return program.description?.trim() || 'Coupon'
  }
  if (program.type === 'TRANSIT_PASS' && !(program.budget > 0)) {
    return 'Transit cards'
  }
  return moneyDetail(program.budget ?? 0, period, currency, conversionRates)
}

const programTags = (program: BaseProgram): MyProgramTag[] => {
  if (program.type === 'TRANSIT_PASS') {
    return (program.transitAgencies ?? []).map((agency) => ({
      id: agency.id,
      label: agency.name,
      iconUrl: agency.iconUrl?.trim() || null,
    }))
  }
  if (program.type === 'MICROMOBILITY') {
    return (program.programMerchants ?? []).map((merchant) => ({
      id: merchant.id,
      label: merchant.name,
      iconUrl: merchant.iconUrl?.trim() || null,
    }))
  }
  return []
}

const qtfLimit = (
  program: PreTaxProgram | null,
  irsTaxLimit: number | null
): number | null => {
  if (!program) return null
  const limit = program.employerMaxAllocationLimit ?? irsTaxLimit
  return limit != null && limit > 0 ? limit : null
}

const qtfRow = (input: {
  transit: PreTaxProgram | null
  parking: PreTaxProgram | null
  irsTaxLimit: number | null
  currency: CurrencyType
  conversionRates: ConversionRates
}): MyProgramRow | null => {
  const present = [input.transit, input.parking].filter(
    (program): program is PreTaxProgram =>
      !!program && program.status !== 'CANCELLED' && program.status !== 'CANCELLING'
  )
  if (present.length === 0) return null

  const limits = present
    .map((program) => qtfLimit(program, input.irsTaxLimit))
    .filter((limit): limit is number => limit != null)
  const upTo = limits.length > 0 ? Math.max(...limits) : null

  return {
    id: 'qtf',
    name: 'Commuter Pre-Tax',
    detail:
      upTo == null
        ? 'Tax-free commuter benefits'
        : `Up to ${formatDashboardMoney(
            upTo,
            input.currency,
            input.conversionRates
          )}/mo tax-free`,
    tags: [],
    pill: 'pre-tax',
    enrollment: present.every((program) => program.status === 'PENDING')
      ? 'available'
      : 'enrolled',
    type: 'QTF',
    couponCode: null,
  }
}

export const myProgramRows = (input: {
  programs: BaseProgram[]
  transit: PreTaxProgram | null
  parking: PreTaxProgram | null
  isContractor: boolean
  irsTaxLimit: number | null
  currency?: CurrencyType
  conversionRates?: ConversionRates
}): MyProgramRow[] => {
  const currency = input.currency || 'USD'
  const conversionRates = input.conversionRates ?? {}
  const rows: MyProgramRow[] = []
  if (!input.isContractor) {
    const pretax = qtfRow({
      transit: input.transit,
      parking: input.parking,
      irsTaxLimit: input.irsTaxLimit,
      currency,
      conversionRates,
    })
    if (pretax) rows.push(pretax)
  }

  input.programs
    .filter(
      (program) =>
        isEmployeeVisibleProgram(program.status) &&
        (program.type !== 'TRANSIT_PASS' || program.transitPassEnrolled === true)
    )
    .forEach((program) => {
      rows.push({
        id: program.id,
        name: getProgramDisplayName(program),
        detail: programDetail(program, currency, conversionRates),
        tags: programTags(program),
        pill: 'employer-paid',
        enrollment: program.status === 'PENDING' ? 'available' : 'enrolled',
        type: program.type,
        couponCode: program.couponCode?.trim() || null,
      })
    })

  return rows.sort((a, b) => {
    const typeOrder =
      PROGRAM_TYPE_ORDER.indexOf(a.type) - PROGRAM_TYPE_ORDER.indexOf(b.type)
    if (typeOrder !== 0) return typeOrder
    return a.name.localeCompare(b.name)
  })
}

export const unenrolledTransitPasses = (programs: BaseProgram[]): BaseProgram[] =>
  programs
    .filter(
      (program) =>
        program.type === 'TRANSIT_PASS' &&
        isEmployeeVisibleProgram(program.status) &&
        program.transitPassEnrolled !== true
    )
    .sort((a, b) =>
      getProgramDisplayName(a).localeCompare(getProgramDisplayName(b))
    )

const modeLabel = (modeType: string | undefined): string | null => {
  if (!modeType) return null
  if (TRANSIT_MODE_TYPES.has(modeType)) return 'transit'
  if (BIKE_MODE_TYPES.has(modeType)) return 'bike'
  return modeType.toLowerCase()
}

export const monthTripBucket = (
  trips: { [month: string]: TripMonth } | { [month: number]: TripMonth } | null
): TripMonth | null => {
  if (!trips) return null
  const keyed = trips as { [month: string]: TripMonth }
  const key = moment().format('YYYY-MM')
  if (keyed[key]) return keyed[key]
  const values = Object.values(keyed)
  return values.length === 1 ? values[0] : null
}

export const summarizeTrips = (
  bucket: TripMonth | null,
  modes: { [modeId: string]: Mode } | null
): { total: number; breakdown: string } => {
  if (!bucket?.trips) return { total: 0, breakdown: '' }
  const counts: { [label: string]: number } = {}
  let total = 0
  Object.values(bucket.trips).forEach((dayTrips) => {
    dayTrips.forEach((trip) => {
      if (trip.noTripReason) return
      total += 1
      const modeId = trip.modeIds?.[0]
      const label = modeLabel(modeId ? modes?.[modeId]?.modeType : undefined)
      if (!label) return
      counts[label] = (counts[label] || 0) + 1
    })
  })
  const breakdown = Object.entries(counts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label, count]) => `${count} ${label}`)
    .join(', ')
  return { total, breakdown }
}

type SpendRow = {
  amount?: number
  type?: unknown
  approved?: boolean | null
}

const postedAmount = (transactions: SpendRow[]): number =>
  transactions.reduce((sum, tx) => {
    const amount = tx.amount ?? 0
    if (String(tx.type || '').toLowerCase() === 'refund') {
      return sum - Math.abs(amount)
    }
    return sum + amount
  }, 0)

// Pending rows stay out of the posted list until capture. Count only approved
// ones so a decline does not look like spend. A payload without `approved`
// is an older response; those pending rows are still in-flight spend.
const pendingAmount = (authorizations: SpendRow[]): number =>
  authorizations.reduce((sum, auth) => {
    if (auth.approved === false || auth.approved === null) return sum
    return sum + (auth.amount ?? 0)
  }, 0)

export const totalPostedSpend = (
  transactions: SpendRow[] | null,
  authorizations: SpendRow[] | null = null
): number | null => {
  if (!transactions && !authorizations) return null
  return (
    postedAmount(transactions ?? []) + pendingAmount(authorizations ?? [])
  )
}
